
import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { format } from 'date-fns';
import type { TrendGranularity, TrendPoint, TenantTrendPoint } from '@/services/costAnalyticsService';


export const CATEGORY_META: Record<string, { label: string; color: string }> = {
  text_generation: { label: 'Text Generation', color: '#7c3aed' },
  image_generation: { label: 'Image Generation', color: '#ff7849' },
  embeddings: { label: 'Embeddings', color: '#06b6d4' },
  text_to_speech: { label: 'Text to Speech', color: '#22c55e' },
  speech_to_text: { label: 'Speech to Text', color: '#f59e0b' },
  web_search: { label: 'Web Search', color: '#6366f1' },
  video: { label: 'Video', color: '#ef4444' },
  storage: { label: 'Storage', color: '#64748b' },
  knowledge_store: { label: 'Knowledge Store', color: '#0891b2' },
  other: { label: 'Other', color: '#94a3b8' },
};
export const categoryLabel = (key: string) => CATEGORY_META[key]?.label || key;
export const categoryColor = (key: string) => CATEGORY_META[key]?.color || '#94a3b8';

export const FEATURE_LABELS: Record<string, string> = {
  course: 'Course', certification: 'Certification', learning_path: 'Learning Path',
  trending_topics: 'Trending Topic',
  ai_assistant: 'AI Assistant',
  document_store: 'Document Store', other: 'Other',
};
export const featureLabel = (k: string) => FEATURE_LABELS[k] || prettify(k);

// One-line scope summary for an export-history row.
export const exportScopeLabel = (e: { start_date?: string; end_date?: string; tenant_keys?: string[]; service_categories?: string[] }) => {
  const range = e.start_date ? `${e.start_date} → ${e.end_date}` : 'All time';
  const tenant = e.tenant_keys?.length ? e.tenant_keys.join(', ') : 'All tenants';
  const cat = e.service_categories?.length ? e.service_categories.map(categoryLabel).join(', ') : 'All categories';
  return `${range} · ${tenant} · ${cat}`;
};
export const prettify = (k: string) => k.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export const mergeByLowerKey = (obj: Record<string, { cost?: number }> = {}): Record<string, { cost: number }> => {
  const out: Record<string, { cost: number }> = {};
  for (const [k, v] of Object.entries(obj || {})) {
    const lk = k.toLowerCase();
    (out[lk] = out[lk] || { cost: 0 }).cost += (v?.cost || 0);
  }
  return out;
};


export const fmtCurrency = (v = 0): string =>
  v > 0 && v < 0.0001 ? `$${v.toPrecision(2)}`                  // sub-cent tiny → keep significant digits (e.g. $0.0000050)
    : v > 0 && v < 1 ? `$${v.toFixed(4)}`                        // sub-dollar → 4 dp
    : `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtNumber = (v = 0): string => v.toLocaleString();
export const fmtTokens = (v = 0): string =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(2)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(1)}K` : `${v}`;

//  Cost format (Dollar ↔ Credit)

export type CostFormat = 'dollar' | 'credit';

// Render raw credits, e.g. "6 credits" / "0.75 credits".
export const fmtCredits = (v = 0): string => {
  const n = v > 0 && v < 0.0001 ? v.toPrecision(2)
    : v > 0 && v < 1 ? v.toFixed(4)
    : v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${n} credits`;
};

// Build a display formatter for a USD amount. In 'credit' mode it converts to credits;

export const makeCostFmt = (format: CostFormat, dollarValue?: number) =>
  (usd = 0): string =>
    format === 'credit' && dollarValue && dollarValue > 0
      ? fmtCredits(usd / dollarValue)
      : fmtCurrency(usd);


export function CoinIcon({ className = 'w-4 h-4' }: { className?: string }) {

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}
      strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden>
      <ellipse cx="12" cy="6" rx="8" ry="3" />
      <path d="M4 6v12c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
      <path d="M4 10c0 1.66 3.58 3 8 3s8-1.34 8-3" />
      <path d="M4 14c0 1.66 3.58 3 8 3s8-1.34 8-3" />
    </svg>
  );
}


export function CostValue({ usd = 0, format, dollarValue }: { usd?: number; format: CostFormat; dollarValue?: number }) {
  if (format === 'credit' && dollarValue && dollarValue > 0) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <CoinIcon className="w-4 h-4 text-amber-500 shrink-0" />
        {fmtCredits(usd / dollarValue)}
      </span>
    );
  }
  return <>{fmtCurrency(usd)}</>;
}

// Vibrant diagonal-striped gradient fill for progress bars (Cost by Feature / Cost by Tenant).
export const BAR_FILL =
  'repeating-linear-gradient(45deg, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 7px, transparent 7px, transparent 14px), linear-gradient(90deg, rgb(var(--brand-violet-600)) 0%, rgb(var(--brand-purple-500)) 55%, rgb(var(--brand-pink-500)) 100%)';

export const PRESETS = [
  { key: '7d', label: 'Last 7 days', days: 7 },
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '180d', label: 'Last 6 months', days: 180 },
  { key: '365d', label: 'Last 1 year', days: 365 },
  { key: 'all', label: 'All time', days: 0 },   // no dates → backend returns the full history
];

export type TrendMetric = 'total_cost' | 'request_count' | 'total_tokens';
export const TREND_META: Record<TrendMetric, { label: string; color: string }> = {
  total_cost: { label: 'Cost', color: '#7c3aed' },
  request_count: { label: 'Requests', color: '#06b6d4' },
  total_tokens: { label: 'Tokens', color: '#22c55e' },
};
// Distinct line colors for the per-tenant trend (top 6 + Others).
export const TENANT_LINE_COLORS = ['#7c3aed', '#06b6d4', '#22c55e', '#f59e0b', '#ef4444', '#6366f1', '#0891b2'];

// Trend granularity is chosen automatically from the date range so the chart is
// always readable: short range → daily, medium → weekly, long → monthly.
export function autoGranularity(start?: string, end?: string): TrendGranularity {
  if (!start || !end) return 'monthly';                 // "All time" (no dates) → month buckets
  const days = Math.max(1, Math.round((new Date(`${end}T00:00:00Z`).getTime() - new Date(`${start}T00:00:00Z`).getTime()) / 86_400_000) + 1);
  return days <= 31 ? 'daily' : days <= 180 ? 'weekly' : 'monthly';
}


export function isoWeekKey(date: Date): string {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7) + 3); // Thursday of this ISO week
  const y = d.getUTCFullYear();
  const ft = new Date(Date.UTC(y, 0, 4));
  ft.setUTCDate(ft.getUTCDate() - ((ft.getUTCDay() + 6) % 7) + 3);
  const week = 1 + Math.round((d.getTime() - ft.getTime()) / (7 * 86_400_000));
  return `${y}-W${String(week).padStart(2, '0')}`;
}
export const isoWeekMonday = (isoYear: number, week: number): Date => {
  const jan4 = new Date(Date.UTC(isoYear, 0, 4));
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (week - 1) * 7);
  return monday;
};


export function buildPeriodKeys(start: string, end: string, gran: TrendGranularity): string[] {
  const keys: string[] = [];
  const s = new Date(`${start}T00:00:00Z`); const e = new Date(`${end}T00:00:00Z`);
  if (gran === 'daily') {
    for (const d = new Date(s); d <= e; d.setUTCDate(d.getUTCDate() + 1)) keys.push(d.toISOString().slice(0, 10));
  } else if (gran === 'monthly') {
    for (const d = new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), 1)); d <= e; d.setUTCMonth(d.getUTCMonth() + 1))
      keys.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`);
  } else {
    const mon = new Date(s); mon.setUTCDate(s.getUTCDate() - ((s.getUTCDay() + 6) % 7)); // Monday of start week
    for (const d = mon; d <= e; d.setUTCDate(d.getUTCDate() + 7)) keys.push(isoWeekKey(d));
  }
  return keys;
}


export function formatPeriodLabel(key: string, gran: TrendGranularity): string {
  try {
    if (gran === 'monthly') {
      const [y, m] = key.split('-').map(Number);
      const first = new Date(y, m - 1, 1);
      const last = new Date(y, m, 0);                 // last day of the month
      return `${format(first, 'MMM d')} – ${format(last, 'MMM d')}`;
    }
    if (gran === 'weekly') {
      const mt = key.match(/^(\d{4})-W(\d{2})$/);
      if (!mt) return key;
      const mon = isoWeekMonday(Number(mt[1]), Number(mt[2]));
      const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 6);   // Mon → Sun = the full week
      return `${format(mon, 'MMM d')} – ${format(sun, 'MMM d')}`;
    }
    return format(new Date(`${key}T00:00:00`), 'MMM d');
  } catch { return key; }
}


export function buildTrendSeries(opts: {
  tenantKey?: string;
  startDate?: string;
  endDate?: string;
  granularity: TrendGranularity;
  trend: TrendPoint[];
  tenantTrend: TenantTrendPoint[];
  trendMetric: TrendMetric;
}): { trendLabels: string[]; trendSeries: { name: string; data: number[]; color: string }[] } {
  const { tenantKey, startDate, endDate, granularity, trend, tenantTrend, trendMetric } = opts;
  const round = (v: number) => Number((v || 0).toFixed(trendMetric === 'total_cost' ? 4 : 0));
  const periods = (startDate && endDate)
    ? buildPeriodKeys(startDate, endDate, granularity)
    : Array.from(new Set((tenantKey ? trend.map((p) => p.period) : tenantTrend.map((r) => r.period)))).sort();
  const labels = periods.map((p) => formatPeriodLabel(p, granularity));
  if (tenantKey) {
    const byPeriod = new Map(trend.map((p) => [p.period, p]));
    // Tokens view → split into Input / Output / Total so the breakdown is visible per period.
    if (trendMetric === 'total_tokens') {
      const line = (name: string, key: 'prompt_tokens' | 'completion_tokens' | 'total_tokens', color: string) => ({
        name, color, data: periods.map((p) => round((byPeriod.get(p)?.[key] as number) || 0)),
      });
      return {
        trendLabels: labels,
        trendSeries: [
          line('Input', 'prompt_tokens', '#10b981'),
          line('Output', 'completion_tokens', '#f59e0b'),
          line('Total', 'total_tokens', '#22c55e'),
        ],
      };
    }
    return {
      trendLabels: labels,
      trendSeries: [{
        name: TREND_META[trendMetric].label,
        data: periods.map((p) => round((byPeriod.get(p)?.[trendMetric] as number) || 0)),
        color: TREND_META[trendMetric].color,
      }],
    };
  }
  const byTenant = new Map<string, { name: string; total: number; points: Map<string, number> }>();
  for (const r of tenantTrend) {
    const v = (r[trendMetric] as number) || 0;
    let e = byTenant.get(r.tenant_key);
    if (!e) { e = { name: r.tenant_name || r.tenant_key, total: 0, points: new Map() }; byTenant.set(r.tenant_key, e); }
    e.total += v;
    e.points.set(r.period, (e.points.get(r.period) || 0) + v);
  }
  const sorted = [...byTenant.values()].sort((a, b) => b.total - a.total);
  const series = sorted.slice(0, 6).map((t, i) => ({
    name: t.name, color: TENANT_LINE_COLORS[i % TENANT_LINE_COLORS.length],
    data: periods.map((p) => round(t.points.get(p) || 0)),
  }));
  const others = sorted.slice(6);
  if (others.length) {
    series.push({ name: 'Others', color: '#cbd5e1', data: periods.map((p) => round(others.reduce((s, o) => s + (o.points.get(p) || 0), 0))) });
  }
  return { trendLabels: labels, trendSeries: series };
}


export function ShareBar({ pct }: { pct: number }) {
  const width = Math.min(100, Math.max(2, pct));
  return (
    <div className="group relative">
      <div className="h-3.5 bg-gray-100 rounded-full overflow-hidden border border-gray-300/70">
        <div
          className="h-full rounded-full shadow-[0_1px_5px_rgb(var(--brand-purple-500)/0.5)] transition-[width] duration-700 ease-out"
          style={{ width: `${width}%`, backgroundImage: BAR_FILL }}
        />
      </div>
      <div className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 z-10 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-150">
        <span className="relative inline-block px-2.5 py-1 rounded-lg bg-gradient-to-r from-brand-violet-600 to-brand-pink-600 text-white text-[11px] font-bold shadow-lg whitespace-nowrap">
          {pct.toFixed(1)}% of total
          <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 rotate-45 bg-brand-pink-600" />
        </span>
      </div>
    </div>
  );
}

export interface KpiCardProps {
  title: string; value: React.ReactNode; icon: React.ReactNode; tint: string;
  change?: number; trend?: 'up' | 'down'; loading?: boolean;
}
export function KpiCard({ title, value, icon, tint, change, trend, loading }: KpiCardProps) {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 animate-pulse">
        <div className="w-11 h-11 bg-gray-200 rounded-xl mb-4" />
        <div className="h-7 bg-gray-200 rounded w-28 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-20" />
      </div>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <span className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>{icon}</span>
        {change != null && (
          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${trend === 'up' ? 'text-red-500' : 'text-emerald-600'}`}>
            {trend === 'up' ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}{Math.abs(change)}%
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mt-1">{title}</div>
    </div>
  );
}
